/**
 * Password validation utility functions
 */

/**
 * Password validation rules with their regular expressions
 */
export const passwordRules = [
  { id: 'length', label: 'Minimum 12 characters', regex: /.{12,}/, weight: 15 },
  { id: 'uppercase', label: 'At least one uppercase letter', regex: /[A-Z]/, weight: 15 },
  { id: 'lowercase', label: 'At least one lowercase letter', regex: /[a-z]/, weight: 15 },
  { id: 'number', label: 'At least one number', regex: /[0-9]/, weight: 15 },
  { id: 'special', label: 'At least one special character', regex: /[^A-Za-z0-9]/, weight: 15 },
  { id: 'norepeat', label: 'No more than 2 repeated characters in a row', regex: /^(?!.*(.)\1\1).+$/, weight: 10 },
  // Custom validator for common passwords
  {
    id: 'nocommon',
    label: 'Not a commonly used password',
    weight: 15,
    validator: (password: string) => {
      const commonPasswords = [
        'Password123!', 
        'Admin123!', 
        'Welcome123!', 
        'Company123!', 
        'qwerty123!',
        'Letmein123!',
        'P@ssw0rd',
        'Password1',
        'Qwerty123',
        '123456789aA!',
        'Test123!',
      ];
      return !commonPasswords.includes(password);
    }
  }
];

/**
 * Interface for the result of a password validation
 */
export interface PasswordValidationResult {
  isValid: boolean;
  score: number;
  failedRules: string[];
  passedRules: string[];
  strengthLevel: 'very-weak' | 'weak' | 'moderate' | 'strong' | 'very-strong';
}

/**
 * Validates a password against the defined rules
 * @param password The password to validate
 * @returns A validation result object
 */
export function validatePassword(password:  string): PasswordValidationResult {
  const failedRules: string[] = [];
  const passedRules: string[] = [];
  let score = 0;
  let totalWeight = 0;
  
  // Check each rule
  passwordRules.forEach(rule => {
    totalWeight += rule.weight;
    let passed = false;
    
    if (rule.regex) {
      passed = rule.regex.test(password);
    } else if (rule.validator) {
      passed = rule.validator(password);
    }
    
    if (passed) {
      score += rule.weight;
      passedRules.push(rule.id);
    } else {
      failedRules.push(rule.id);
    }
  });
  
  // Add bonus for length
  if (password.length > 15) score += 5;
  if (password.length > 20) score += 5;
  
  // Normalize score to 0-100
  const normalizedScore = Math.min(100, Math.floor((score / totalWeight) * 100));
  
  // Determine strength level
  let strengthLevel: 'very-weak' | 'weak' | 'moderate' | 'strong' | 'very-strong';
  if (normalizedScore >= 90) strengthLevel = 'very-strong';
  else if (normalizedScore >= 70) strengthLevel = 'strong';
  else if (normalizedScore >= 50) strengthLevel = 'moderate';
  else if (normalizedScore >= 30) strengthLevel = 'weak';
  else strengthLevel = 'very-weak';
  
  // Password is valid if all required rules pass
  const requiredRules = passwordRules.filter(r => r.id !== 'norepeat'); // Example: norepeat might be optional
  const isValid = requiredRules.every(rule => 
    rule.regex ? rule.regex.test(password) : rule.validator ? rule.validator(password) : false
  );
  
  return {
    isValid,
    score: normalizedScore,
    failedRules,
    passedRules,
    strengthLevel
  };
}

/**
 * Generates feedback based on failed rules
 * @param validationResult The validation result
 * @returns A string with feedback about what needs to be fixed
 */
export function getPasswordFeedback(validationResult:  PasswordValidationResult): string {
  if (validationResult.isValid) {
    return 'Password meets all requirements.';
  }
  
  const failedRuleLabels = validationResult.failedRules.map(id => {
    const rule = passwordRules.find(r => r.id === id);
    return rule ? rule.label : '';
  }).filter(Boolean);
  
  if (failedRuleLabels.length === 0) {
    return 'Please check your password.';
  }
  
  return `Your password doesn't meet the following requirements: ${failedRuleLabels.join(', ')}.`;
}

/**
 * Check if a password might be compromised
 * In a real app, this would call an API like "Have I Been Pwned"
 * @param password The password to check
 * @returns A promise resolving to true if the password might be compromised
 */
export async function checkCompromisedPassword(password:  string): Promise<boolean> {
  // This is just a simulation
  // In a real app, you would call an API that checks against known data breaches
  // without sending the full password (e.g., using k-anonymity with HIBP)
  
  // Simulate API call
  return new Promise((resolve) => {
    setTimeout(() => {
      // For demo purposes, consider passwords shorter than 12 chars as compromised
      resolve(password.length < 12);
    }, 500);
  });
}

export default {
  validatePassword,
  getPasswordFeedback,
  checkCompromisedPassword,
  passwordRules
};
